import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { transaction, withTransaction } from '@datorama/akita';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { BaseTransaction, Transaction } from './transaction.model';
import { TransactionsStore } from './transactions.store';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { TransformLegacyService } from './transform-legacy.service';
import { Observable, from, of } from 'rxjs';
import { TimeSeriesDataService } from '../../time-series-data/state/time-series-data.service';
import {
    BaseTimeSeriesData,
    LegacyMarketData
} from '../../time-series-data/state/time-series-data.model';
import { ToastService } from '../../../shared/services/toast/toast.service';
import { InstrumentsService } from 'src/app/shared/state/instruments/instruments.service';
import { Instrument } from '../../../shared/state/instruments/instrument.model';
import { Acct } from 'src/app/shared/state/accts/acct.model';

@Injectable({ providedIn: 'root' })
export class TransactionsService {
    constructor(
        private transactionsStore: TransactionsStore,
        private http: HttpClient,
        private appConfig: AppConfigQuery,
        private transformLegacyService: TransformLegacyService,
        private timeSeriesDataService: TimeSeriesDataService,
        private toastService: ToastService,
        private instrumentsService: InstrumentsService
    ) {
        // Blystad specific hotfix #SWIM-4883 for Private Equity
        //this.get().subscribe();
    }

    @transaction()
    upsertTransactions(transactions: Transaction[]) {
        this.transactionsStore.set(transactions);
        this.transactionsStore.update((state) => ({
            ...state,
            ui: { lastEntryByUser: new Date() }
        }));
    }

    get() {
        return this.http
            .get<Transaction[]>(
                `${this.appConfig.get().gatewayBaseUrl}/view/transactions/v1`,
                {
                    params: {
                        showCancelled: 'true'
                    }
                }
            )
            .pipe(
                tap((entities) => {
                    this.upsertTransactions(entities);
                })
            );
    }

    transferInstrument(transaction) {
        return this.http.post(
            `${this.appConfig.get().gatewayBaseUrl}/transactions/transfer`,
            transaction
        );
    }

    clearTransactions() {
        this.transactionsStore.reset();
        this.transactionsStore.setLoading(false);
    }

    getTransactionsBetween(from: Date, to: Date) {
        this.transactionsStore.setLoading(true);
        return this.http
            .get<Transaction[]>(
                `${this.appConfig.get().gatewayBaseUrl}/view/transactions/v1`,
                {
                    params: {
                        fromDate: from.toISOString(),
                        toDate: to.toISOString()
                    }
                }
            )
            .pipe(
                withTransaction((entities) => {
                    this.transactionsStore.set(entities);
                    this.transactionsStore.setLoading(false);
                })
            );
    }

    /** @deprecated - should be avoided as it will just add transactions to the store without considering what's already in it */
    getTransactionsForAccountIds(accountIds: Array<Number>) {
        return this.http
            .get<Transaction[]>(
                `${
                    this.appConfig.get().gatewayBaseUrl
                }/view/transactions/v1?accountIds=${accountIds.join(',')}`,
                {
                    params: {
                        showCancelled: 'true'
                    }
                }
            )
            .pipe(
                tap((entities) => {
                    this.transactionsStore.add(entities);
                })
            );
    }

    /** @deprecated - should be avoided as it will just add transactions to the store without considering what's already in it */
    // Blystad specific hotfix #SWIM-4883 for Private Equity
    getTransactionsForUnderlyingInstrumentIds(underlyingInstrumentIds: Array<Number>) {
        return this.http
            .get<Transaction[]>(
                `${
                    this.appConfig.get().gatewayBaseUrl
                }/view/transactions/v1?underlyingInstrumentIds=${underlyingInstrumentIds.join(
                    ','
                )}`,
                {
                    params: {
                        showCancelled: 'true'
                    }
                }
            )
            .pipe(
                tap((entities) => {
                    this.transactionsStore.add(entities);
                })
            );
    }

    /** @deprecated - should be avoided as it will just add transactions to the store without considering what's already in it */
    // Blystad specific hotfix #SWIM-4883 for Private Equity
    getTransactionsForUnderlyingInstrumentId(underlyingInstrumentId: Number) {
        return this.http
            .get<Transaction[]>(
                `${
                    this.appConfig.get().gatewayBaseUrl
                }/view/transactions/v1?underlyingInstrumentIds=${underlyingInstrumentId}`,
                {
                    params: {
                        showCancelled: 'true'
                    }
                }
            )
            .pipe(
                tap((entities) => {
                    this.transactionsStore.add(entities);
                })
            );
    }

    /** @deprecated - should be avoided as it will just add transactions to the store without considering what's already in it */
    // Blystad specific hotfix #SWIM-4883 for Private Equity
    getTransactionsForInstrumentId(instrumentId: Number) {
        return this.http
            .get<Transaction[]>(
                `${
                    this.appConfig.get().gatewayBaseUrl
                }/view/transactions/v1?instrumentIds=${instrumentId}`,
                {
                    params: {
                        showCancelled: 'true'
                    }
                }
            )
            .pipe(
                tap((entities) => {
                    this.transactionsStore.add(entities);
                })
            );
    }

    getForInstrumentIds({
        instrumentIds,
        accountIds,
        instrumentType,
        showCancelled
    }: {
        instrumentIds: Array<Instrument['instrumentId']>;
        accountIds?: Array<Acct['accountId']>;
        instrumentType?: Instrument['type'];
        showCancelled?: boolean;
    }): Observable<Transaction[]> {
        const params = {
            instrumentIds: instrumentIds.join(',')
        };
        if (instrumentType) {
            params['instrumentType'] = instrumentType;
        }
        if (accountIds) {
            params['accountIds'] = accountIds.join(',');
        }
        if (showCancelled) {
            params['showCancelled'] = 'true';
        }
        return this.http
            .get<Transaction[]>(
                `${this.appConfig.get().gatewayBaseUrl}/view/transactions/v1`,
                {
                    params
                }
            )
            .pipe(
                withTransaction((entities) => {
                    this.transactionsStore.set(entities);
                    this.transactionsStore.update((state) => ({
                        ...state,
                        ui: { lastEntryByUser: new Date() }
                    }));
                })
            );
    }

    add(baseTransaction: BaseTransaction<any>, type?: string);
    add(transaction: any, type?: string) {
        let url = `${this.appConfig.get().gatewayBaseUrl}/transactions/${
            type || transaction?.transactionType?.toLowerCase().replace(/_/g, '-')
        }`;
        let payload = transaction;

        let updateTimeSeriesData$: Observable<any>;

        // if transaction contains assetType i.e. is of type BaseTransaction...
        if (transaction.assetType) {
            let typedTransaction = transaction as BaseTransaction<any>;
            // ...
            switch (typedTransaction.assetType) {
                case 'PRIVATE_EQUITY':
                    // update url and transform payload
                    if (typedTransaction.instrumentType === 'PE_CONVERTIBLE') {
                        url = `${
                            this.appConfig.get().gatewayBaseUrl
                        }/transactions/private-equity/convertible`;
                        payload =
                            this.transformLegacyService.privateEquityConvertibleTransaction(
                                typedTransaction
                            );
                    } else if (typedTransaction.instrumentType === 'PE_OPTION') {
                        url = `${
                            this.appConfig.get().gatewayBaseUrl
                        }/transactions/private-equity/option`;
                        payload =
                            this.transformLegacyService.privateEquityOptionTransaction(
                                typedTransaction
                            );
                    } else if (
                        typedTransaction.instrumentType === 'PE_SHAREHOLDER_CONTRIBUTION'
                    ) {
                        url = `${
                            this.appConfig.get().gatewayBaseUrl
                        }/transactions/private-equity/shareholder-contribution`;
                        payload =
                            this.transformLegacyService.privateEquityShareholderContributionTransaction(
                                typedTransaction
                            );
                    } else if (typedTransaction.instrumentType === 'PE_LOAN') {
                        url = `${
                            this.appConfig.get().gatewayBaseUrl
                        }/transactions/private-equity/loan`;
                    } else {
                        // Default to single-instrument (share transaction)
                        // value for transaction.assetType is 'PRIVATE_EQUITY'
                        // value for typedTransaction.instrumentType is either 'PE_OTHER_SHARE' or 'PE_PREF_SHARE'
                        url = `${
                            this.appConfig.get().gatewayBaseUrl
                        }/transactions/private-equity/share`;
                        payload =
                            this.transformLegacyService.privateEquityTransaction(
                                typedTransaction
                            );
                    }
                    break;
            }
        }

        // if transaction is of type private fund and contains a new NAV...
        if (
            transaction.instrumentType === 'PRIVATE_FUND' &&
            transaction.newNav !== null &&
            transaction.newNav !== undefined
        ) {
            // ...create a new time series data entry
            const updatedTimeSeriesData: Omit<
                BaseTimeSeriesData<LegacyMarketData>,
                'id' | 'timestamp'
            > = {
                instrumentId: transaction.instrumentId,
                date: new Date(transaction.transactionDate).toISOString().slice(0, 10),
                price: transaction.newNav,
                priceValidationLevel: 'PRELIMINARY'
            };
            // ...and create an observable to update the time series data
            updateTimeSeriesData$ = this.timeSeriesDataService.addLegacyMarketValue(
                updatedTimeSeriesData as BaseTimeSeriesData<LegacyMarketData>
            );
        }
        return this.http.post<Array<Transaction>>(url, payload).pipe(
            withTransaction((transactions) => {
                this.transactionsStore.add(transactions);
                this.transactionsStore.update((state) => ({
                    ...state,
                    ui: { lastEntryByUser: new Date() }
                }));
            }),
            /*
             * In case the transaction generated new instruments we must ensure they're added to the instrument state store.
             * A bit overkill to have to go to the backend to get them since instruments are part the transactions response.
             * But the backend returns different data types from instruments api and transactions registration api.
             *
             * NOTE: In some cases endpoints (legacy) return no body so we need a check for that
             */
            switchMap((transactions) =>
                this._refreshInstrumentsForTransactions(transactions)
            ),
            switchMap((res) => {
                // if add transaction caries a updateTimeSeriesData$ observable...
                return updateTimeSeriesData$
                    ? // ...switch to it and return the original response
                      updateTimeSeriesData$.pipe(
                          map(() => res),
                          tap(() => {
                              if (transaction.instrumentType === 'PRIVATE_FUND') {
                                  // update nav for private fund was successful
                                  this.toastService.show({
                                      intent: 'success',
                                      message: `NAV for fund was successfully updated.`
                                  });
                              }
                          }),
                          // handle error for updating time series data
                          catchError((err) => {
                              if (transaction.instrumentType === 'PRIVATE_FUND') {
                                  // failed to update nav for private fund
                                  this.toastService.show({
                                      intent: 'critical',
                                      message: `Failed to update NAV for fund, please try adding it manually.`
                                  });
                              }
                              return of(res);
                          })
                      )
                    : // ...otherwise return the original response straight away
                      of(res);
            })
        );
    }

    update(id, transaction: Partial<Transaction>) {
        this.transactionsStore.update(id, transaction);
    }

    remove(transactionGroupId: Transaction['transactionGroupId']) {
        return this.http
            .delete<Array<Transaction>>(
                `${this.appConfig.get().gatewayBaseUrl}/transactions`,
                {
                    params: {
                        transactionGroupId
                    }
                }
            )
            .pipe(
                withTransaction((removedTransactions) => {
                    this.transactionsStore.upsertMany(removedTransactions);
                    this.transactionsStore.update((state) => ({
                        ...state,
                        ui: { lastEntryByUser: new Date() }
                    }));
                })
            );
    }

    restore(transactionGroupId: Transaction['transactionGroupId']) {
        return this.http
            .post<Array<Transaction>>(
                `${this.appConfig.get().gatewayBaseUrl}/transactions/restore`,
                null,
                {
                    params: {
                        transactionGroupId
                    }
                }
            )
            .pipe(
                withTransaction((restoredTransactions) => {
                    this.transactionsStore.remove(
                        restoredTransactions.map(
                            (restoredTransaction) =>
                                restoredTransaction.previousTransactionId
                        )
                    );
                    this.transactionsStore.upsertMany(restoredTransactions);
                    this.transactionsStore.update((state) => ({
                        ...state,
                        ui: { lastEntryByUser: new Date() }
                    }));
                })
            );
        //.pipe(switchMap((_) => this.get()));
    }

    _refreshInstrumentsForTransactions(transactions: Transaction<any>[]) {
        if (transactions && transactions.length > 0) {
            return from(transactions).pipe(
                /*
                 * Ensure completion in sequence.
                 * getById will fetch instruments and update state store; Ignore the return value here.
                 */
                concatMap((ts) =>
                    this.instrumentsService.getById(ts.instrument.instrumentId)
                ),
                map((i) => of(transactions))
            );
        } else return of(transactions);
    }
}
